import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { GLOSSARY_TERMS } from "@/lib/glossary";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/glossary/")({
  head: () => {
    const url = `${SITE.url}/glossary`;
    return {
      meta: [
        { title: `Local SEO Glossary — Map Pack, GBP & AI Search Terms | ${SITE.name}` },
        {
          name: "description",
          content:
            "A working glossary of local SEO, Google Business Profile, Google Maps, and AI search terms — written for SEO agencies and local business owners. Cite freely.",
        },
        { property: "og:title", content: `Local SEO Glossary | ${SITE.name}` },
        { property: "og:description", content: "Map Pack, GBP, AI Overviews, Ask Maps — explained for agencies and operators." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            name: "Local SEO Glossary",
            url,
            hasDefinedTerm: GLOSSARY_TERMS.map((t) => ({
              "@type": "DefinedTerm",
              name: t.term,
              description: t.definition,
              url: `${url}/${t.slug}`,
              inDefinedTermSet: url,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Glossary", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: GlossaryPage,
});

function GlossaryPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li>Glossary</li>
          </ol>
        </nav>
        <header className="mt-6">
          <p className="eyebrow">Reference</p>
          <h1 className="headline-display mt-3 text-5xl">Local SEO glossary</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            The working vocabulary of Google Business Profile, the Map Pack, and AI search —
            written for SEO agencies, in-house teams, and the operators behind the listings.
          </p>
        </header>

        <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
          <CiteThis title="Local SEO Glossary" url={`${SITE.url}/glossary`} />
          <span className="text-xs text-muted-foreground">
            Each term has its own permalink — link to the one you cite.
          </span>
        </div>

        <dl className="mt-12 space-y-8">
          {GLOSSARY_TERMS.map((t) => (
            <div key={t.slug} id={t.slug} className="scroll-mt-24">
              <dt className="font-semibold text-lg">
                <Link to="/glossary/$slug" params={{ slug: t.slug }} className="hover:text-accent">
                  {t.term}
                </Link>
                {t.also && <span className="ml-2 text-xs font-normal uppercase tracking-wider text-muted-foreground">also: {t.also}</span>}
              </dt>
              <dd className="mt-2 text-base leading-relaxed text-foreground/85">
                {t.definition}{" "}
                <Link to="/glossary/$slug" params={{ slug: t.slug }} className="text-xs uppercase tracking-wider text-ochre-deep hover:text-accent ml-1">
                  Permalink →
                </Link>
              </dd>
            </div>
          ))}
        </dl>
        <NewsletterCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
